/**
 * Canonical identity-role resolution (single source of truth).
 *
 * The AX1 Blueprint defines exactly six canonical ACCOUNT roles across two
 * independent authority planes:
 *
 *   Customer organization plane : organization_owner | organization_admin | organization_member
 *   Platform control plane      : platform_owner | platform_admin | support_operator
 *
 * This module replaces the ad-hoc, per-component precedence expressions such as
 * `platform_role ?? account_role ?? role` that previously (a) normalized valid
 * organization roles to `null` (producing empty navigation) and (b) diverged
 * between Sidebar, Command Palette, Role Switcher, route registry and redirects.
 *
 * Doctrine enforced here:
 *  - Account role and platform role are resolved INDEPENDENTLY and returned
 *    separately. A platform role never becomes a customer account role and a
 *    customer account role never becomes a platform role.
 *  - The canonical `account_role` field selects exactly one authority plane.
 *  - `platform_role`, `role` and `workspace_role` are read through explicit,
 *    fail-closed compatibility adapters only when no canonical account role is
 *    present. They never override or add a second plane to a canonical value.
 *  - Unknown / unmapped values fail closed (both planes null, `isUnknown` set).
 *    They never silently become organization_member or an administrator.
 */
import type { User } from '@/types';
import type { OrganizationAccountRole, PlatformAccountRole } from '@/types/authority';
import {
  MVP_ORGANIZATION_ACCOUNT_ROLES,
  MVP_PLATFORM_ACCOUNT_ROLES,
} from '@/types/authority';
import { resolveMvpAccountRole } from './accountRoles';

export interface ResolvedIdentityRoles {
  /** Canonical customer-plane account role, or null when none applies. */
  readonly accountRole: OrganizationAccountRole | null;
  /** Canonical control-plane role, or null when none applies. */
  readonly platformRole: PlatformAccountRole | null;
  /** True when at least one plane resolved to a canonical role. */
  readonly isKnown: boolean;
  /** True when a role value was present but could not be classified (fail-closed). */
  readonly isUnknown: boolean;
}

const ORGANIZATION_ROLE_SET: ReadonlySet<string> = new Set<string>(MVP_ORGANIZATION_ACCOUNT_ROLES);
const PLATFORM_ROLE_SET: ReadonlySet<string> = new Set<string>(MVP_PLATFORM_ACCOUNT_ROLES);

function normalizeRoleToken(role: string | null | undefined): string {
  return (role ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function isOrganizationAccountRole(
  role: string | null | undefined,
): role is OrganizationAccountRole {
  return Boolean(role && ORGANIZATION_ROLE_SET.has(role));
}

export function isPlatformAccountRole(
  role: string | null | undefined,
): role is PlatformAccountRole {
  return Boolean(role && PLATFORM_ROLE_SET.has(role));
}

/**
 * Compatibility adapter: map a legacy `platform_role` value (which may carry an
 * old functional label such as `system_admin`/`security_admin`) to one of the
 * three canonical platform ACCOUNT roles. Functional specialization is NOT an
 * authority level — every non-owner, non-support platform label collapses to
 * `platform_admin`, and the functional label survives only as an overlay.
 * Fails closed for unknown values.
 */
export function adaptLegacyPlatformRole(
  platformRole: string | null | undefined,
): PlatformAccountRole | null {
  const token = normalizeRoleToken(platformRole);
  if (!token) return null;
  if (token === 'platform_owner' || token === 'owner' || token === 'super_admin') {
    return 'platform_owner';
  }
  if (token === 'support_operator' || token === 'support_admin') {
    return 'support_operator';
  }
  if (
    token === 'platform_admin'
    || token === 'system_admin'
    || token === 'security_admin'
    || token === 'commercial_admin'
    || token === 'implementation_admin'
    || token === 'admin'
    || token === 'administrator'
  ) {
    return 'platform_admin';
  }
  return null; // fail closed for unknown platform labels
}

/**
 * Compatibility adapter: map a legacy customer role value (`workspace_role` or
 * scalar `role`) to a canonical organization ACCOUNT role. This is ONLY used
 * when no canonical `account_role`/`platform_role` is present (e.g. old
 * fixtures / migration reads). Scoped-relationship slugs (delivery lead,
 * ally, supplier, capital tokens, …) are NOT account assignments and fail closed here.
 */
export function adaptLegacyAccountRole(
  user: Partial<Pick<User, 'workspace_role' | 'role'>> | null | undefined,
): OrganizationAccountRole | null {
  const token = normalizeRoleToken(user?.workspace_role ?? user?.role);
  if (!token) return null;
  switch (token) {
    case 'organization_owner':
    case 'owner':
      return 'organization_owner';
    case 'organization_admin':
    case 'business_admin':
    case 'admin':
    case 'administrator':
      return 'organization_admin';
    case 'organization_member':
    case 'customer_security_admin':
    case 'workspace_admin':
    case 'member':
    case 'team_member':
      return 'organization_member';
    default:
      return null; // scoped relationships / unknown fail closed
  }
}

/**
 * Resolve the canonical account and platform roles for a user, keeping the two
 * authority planes independent. This is the ONLY sanctioned way for navigation,
 * redirects, command palette, role display, dashboard selection and route
 * guards to obtain identity-role facts.
 */
export function resolveIdentityRoles(
  user: Partial<Pick<User, 'account_role' | 'platform_role' | 'workspace_role' | 'role'>> | null | undefined,
): ResolvedIdentityRoles {
  const rawAccount = user?.account_role ?? null;
  const rawPlatform = user?.platform_role ?? null;

  // 1. Canonical account_role is authoritative; split it by plane.
  const canonical = resolveMvpAccountRole(rawAccount);
  let accountRole: OrganizationAccountRole | null =
    canonical && isOrganizationAccountRole(canonical) ? canonical : null;
  let platformRole: PlatformAccountRole | null =
    canonical && isPlatformAccountRole(canonical) ? canonical : null;

  // 2. Legacy platform compatibility applies only when no canonical account
  // role exists. A stale functional scalar must never turn an organization
  // account into a platform operator.
  if (!canonical && rawPlatform) {
    platformRole = adaptLegacyPlatformRole(rawPlatform);
  }

  // 3. Customer-plane compatibility is also legacy-only and never overrides a
  // canonical account role or a recognized legacy platform identity.
  if (!canonical && !platformRole) {
    accountRole = adaptLegacyAccountRole(user);
  }

  const hadRoleValue = Boolean(rawAccount || rawPlatform || user?.workspace_role || user?.role);
  const isKnown = Boolean(accountRole || platformRole);
  return {
    accountRole,
    platformRole,
    isKnown,
    isUnknown: hadRoleValue && !isKnown,
  };
}

/**
 * Convenience: the single role value used to resolve capabilities via
 * `getCapabilitiesFor`. Platform plane takes precedence for capability lookup
 * because a platform account never carries customer capabilities. Returns null
 * (fail closed) when neither plane resolved.
 */
export function resolveCapabilityRole(
  user: Partial<Pick<User, 'account_role' | 'platform_role' | 'workspace_role' | 'role'>> | null | undefined,
): OrganizationAccountRole | PlatformAccountRole | null {
  const { accountRole, platformRole } = resolveIdentityRoles(user);
  return platformRole ?? accountRole;
}

/** True when the resolved identity belongs to the platform control plane. */
export function isPlatformIdentity(
  user: Partial<Pick<User, 'account_role' | 'platform_role' | 'workspace_role' | 'role'>> | null | undefined,
): boolean {
  return resolveIdentityRoles(user).platformRole !== null;
}
